/**
 * Declaration Service - Handles declaration-related API calls
 */

class DeclarationService {
    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    }

    /**
     * Submit final declaration - calls spForm153Insert stored procedure
     * @param {string} appId - Application ID
     * @param {string} orgId - Organization ID
     * @returns {Promise} API response
     */
    async submitFinalDeclaration(appId, orgId, labtypeidfromstorage) {
        try {
            const response = await fetch(`${this.baseURL}/declaration/submit-final-declaration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    appId: appId,
                    orgId: orgId,
                    labtypeid:labtypeidfromstorage
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            return result;
        } catch (error) {
            console.error('Error submitting final declaration:', error);
            throw error;
        }
    }

    /**
     * Save declaration data (auto-save functionality)
     * @param {string} appId - Application ID
     * @param {string} orgId - Organization ID
     * @param {object} declarationData - Declaration form data
     * @returns {Promise} API response
     */
    async saveDeclarationData(appId, orgId, declarationData) {
        try {
            const response = await fetch(`${this.baseURL}/declaration/save-declaration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    appId: appId,
                    orgId: orgId,
                    declarationData: declarationData
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            return result;
        } catch (error) {
            console.error('Error saving declaration data:', error);
            throw error;
        }
    }

    /**
     * Get declaration status
     * @param {string} appId - Application ID
     * @param {string} orgId - Organization ID
     * @returns {Promise} API response
     */
    async getDeclarationStatus(appId, orgId) {
        try {
            const response = await fetch(`${this.baseURL}/declaration/status?appId=${appId}&orgId=${orgId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            return result;
        } catch (error) {
            console.error('Error getting declaration status:', error);
            throw error;
        }
    }

    /**
     * Submit corruption declaration (existing functionality)
     * @param {string} orgId - Organization ID
     * @returns {Promise} API response
     */
    async submitCorruptionDeclaration(orgId) {
        try {
            const response = await fetch(`${this.baseURL}/declaration/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    orgId: orgId
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            return result;
        } catch (error) {
            console.error('Error submitting corruption declaration:', error);
            throw error;
        }
    }

    /**
     * Validate declaration data
     * @param {object} declarationData - Declaration form data
     * @returns {object} Validation result
     */
    validateDeclarationData(declarationData) {
        const errors = [];
        const warnings = [];

        // Check required fields
        if (!declarationData.representativeName || declarationData.representativeName.trim() === '') {
            errors.push('Representative name is required');
        }

        if (!declarationData.representativeTitle || declarationData.representativeTitle.trim() === '') {
            errors.push('Representative title is required');
        }

        if (!declarationData.finalDeclarationAgreed) {
            errors.push('You must agree to the final declaration terms');
        }

        // Check optional but recommended fields
        if (!declarationData.shareholderDirectorFile) {
            warnings.push('Shareholder and Director declaration file is recommended');
        }

        return {
            isValid: errors.length === 0,
            errors: errors,
            warnings: warnings
        };
    }

    /**
     * Format declaration data for API submission
     * @param {object} declarationData - Raw declaration form data
     * @returns {object} Formatted data for API
     */
    formatDeclarationForAPI(declarationData) {
        return {
            representativeName: declarationData.representativeName?.trim() || '',
            representativeTitle: declarationData.representativeTitle?.trim() || '',
            finalDeclarationAgreed: Boolean(declarationData.finalDeclarationAgreed),
            shareholderDirectorFile: declarationData.shareholderDirectorFile || null,
            submissionTimestamp: new Date().toISOString()
        };
    }
}

// Create singleton instance
const declarationService = new DeclarationService();

export default declarationService;